import React, { useMemo } from "react";
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/solid';
import styled from "styled-components";

const TableStyles = styled.table`
  
  [data-sticky-last-left-td]{
    box-shadow: inset -4px 0px 5px -3px #ccc;
  }

  [data-sticky-first-right-td] {
    box-shadow: inset 4px 0px 5px -3px #ccc;
  }
`;



/**
 * 表头th
 * @param { Object } column 【必传】 列的信息
 * @param { string | number } keyIdx 【必传】 唯一的值
 * @param { reactElement } children 子元素
 * @param { string } className class名称
 * @returns 
 */
export const Th = ({column, keyIdx, children, className, tableTheme}) => {
  return (
    <th 
      key={keyIdx} 
      {...column.getHeaderProps(column.getSortByToggleProps())}
      className={`text-xs font-medium ${tableTheme? tableTheme.thColor: 'text-gray-700'} p-4 whitespace-nowrap text-left tracking-wider ${tableTheme? tableTheme.thBgColor: 'bg-gray-100'} ${className}`}
    >
      { children? children : <span className="flex">
        {column.render('Header')}
        {
          column.isSorted ? column.isSortedDesc
          ? <ChevronDownIcon className="w-4" /> : <ChevronUpIcon className="w-4" />
          : ''
        }
      </span>}
    </th>
  )
};

/**
 * 表格头部的tr
 * @param { string | number } keyIdx 【必传】唯一的id 
 * @param { Object } headerGroup reactTable 【必传】的实例属性 
 * @param { String } className class 名称
 * @param { reactElement } children 子元素
 * @returns 
 */
export const HeaderTr = ({keyIdx, headerGroup, className, children, tableTheme}) => {
  return (
    <tr key={keyIdx} {...headerGroup.getHeaderGroupProps()} className={`${className} ${tableTheme? tableTheme.thBgColor: 'bg-gray-100'} relative`}>
      { children && children}
    </tr>
  )
};

/**
 * 表头
 * @param { string } className class名称
 * @param { reactElement } children 子元素
 * @param { Object } headerGroups 【必传】reactTable实例
 * @returns 
 */
export const Thead = ({className, children, headerGroups, tableTheme}) => {
  if( children ) {
    return (
      <thead className={`${tableTheme? tableTheme.thBgColor: 'bg-gray-100'} rounded-t-md sticky top-0 z-10 shadow-md ${className}`} key="thead1">
        { children }
      </thead>
    )
  };

  return (
    <thead className={`${tableTheme? tableTheme.thBgColor: 'bg-gray-100'} rounded-t-md sticky top-0 z-10 shadow-md ${className}`} key="thead2">
      { 
        headerGroups.map((headerGroup, index) => (
          <HeaderTr key={index} headerGroup={headerGroup} tableTheme={tableTheme}>
            { headerGroup.headers.map((column, colIndex) => <Th key={colIndex} tableTheme={tableTheme} column={column} keyIdx={colIndex}></Th>) }
          </HeaderTr>
        ))
      }
    </thead>
  )
};

/**
 * 渲染表格行
 * @param { reactElement } children 子元素 用于自定义tr
 * @param { string } className class名称
 * @param { Object } ...props 额外的参数 一般用于传递给Td组件 ***必须包含{ row：rowInfo }***
 * @returns 
 */
export const Tr = ({ children, className, keyIdx, tableTheme, ...props }) => {
  if( children ) {
    return ( 
      <tr 
        className={`hover:bg-gray-100 ${tableTheme ? tableTheme.bgColor2: 'bg-white'} even:${tableTheme ? tableTheme.bgColor1: 'bg-gray-100'} ${className}`} 
        {...props.row.getRowProps()}
      >
        {children}
      </tr>
    )
  };

  return (
    <tr style={{"minHeight": "3rem"}} className={`hover:bg-gray-100 ${tableTheme ? tableTheme.bgColor2: 'bg-white'} even:${tableTheme ? tableTheme.bgColor1: 'bg-gray-100'} ${className}`} {...props.row.getRowProps()}>
      { props.row.cells.map(( cell, index ) => <Td cell={cell} tableTheme={tableTheme} {...cell.getCellProps()} { ...props }></Td>)}
    </tr>
  )
};

/**
 * 单个单元格组件
 * @param { reactElement } children 子元素
 * @param { string } className class 名称
 * @param { Object } cell 【必传】 单元格 reactTable cell的信息
 * @param { Object } row 【必传】 行 reactRable row的信息
 * @param { Array } expanded 【必传】 是否展开信息 reactRable 实例state中的expanded信息 
 * @param { function } cellRenderCFn 如何去渲染每个单元格的方法 回传3个参数cellRenderCFn(reactTable实例, 当前行, 当前单元格)
 * @param { string | number } key 【必传】 唯一的值
 * @returns 
 */
export const Td = ({ children, className, cell, row, expanded, cellRenderCFn, tableTheme }) => {
  const getCellProps = ({ style, ...restProps }, bgStyle) => {
    return {
      style: { ...style, ...bgStyle },
      ...restProps
    }
  };

  if( children ) {
    return <td
      className={`whitespace-nowrap px-4 py-2 text-sm font-medium ${tableTheme? tableTheme.color: 'text-gray-900'} ${className}`}
      {...cell.getCellProps()}
      {...getCellProps({ ...cell.getCellProps() }, { backgroundColor: "inherit" })}
    >
      {children}
    </td>
  };

  return (
    <td 
      className={`whitespace-nowrap px-4 py-2 text-sm font-medium ${tableTheme? tableTheme.color: 'text-gray-900'} ${className}`}
      {...cell.getCellProps()}
      {...getCellProps({ ...cell.getCellProps() }, { backgroundColor: "inherit" })}
    >
      { cellRenderCFn ? cell.render((instanceRowState) => {
          return cellRenderCFn(instanceRowState, row, cell);
        }): cell.render('Cell', {
          isExpanded: expanded[row.id],
        })
      }
    </td>
  )
};

/**
 * table 渲染内容
 * @param { Object } tableInstance reactTable 的实例 
 * @param { reactElement } renderRowSubComponent 渲染子表格的子组件 
 * @returns 
 */
export const Tbody = ({ tableInstance, renderRowSubComponent, ...props }) => {
  const {visibleColumns, getTableBodyProps, page, prepareRow, state } = tableInstance;
  return (
    <tbody {...getTableBodyProps()}>
      {
        page.map((row, i) => {
          prepareRow(row);
          const rowProps = row.getRowProps();
          return (
            <React.Fragment key={i}>
              <Tr row={row} { ...row.getRowProps()} expanded={state.expanded} { ...props}></Tr>
              {
                row.isExpanded && (
                <tr key={i}>
                   <td colSpan={visibleColumns.length}>
                    {renderRowSubComponent && renderRowSubComponent({ row, rowProps, visibleColumns })}
                    </td>
                </tr>)
              }
            </React.Fragment>
          )
        })
      }
    </tbody>
  )
};

const ReactTable = ({tableInstance, className, LoadingSub, showLoading, ...props}) => {
  const {getTableProps, headerGroups} = tableInstance;

  return (
    <div className={`w-full h-full overflow-hidden flex flex-col ${className ? className : ''}`}>
      <div className="overflow-auto w-full h-full scrollbar relative">
        <TableStyles {...getTableProps()} style={{"minHeight": "15rem"}} className="min-w-full table relative">
          <Thead headerGroups={headerGroups} {...props}></Thead>
          <Tbody tableInstance={tableInstance} {...props}></Tbody>
          {(showLoading && LoadingSub) && <LoadingSub></LoadingSub>}
        </TableStyles>
      </div>
    </div>
  )
};

export default ReactTable;